import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History, ChevronDown, ChevronRight, Copy, Flame, Calendar, Loader2, Bookmark, Trash2, Play, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";

const MEAL_LABELS = {
  bg: { breakfast: "Закуска", lunch: "Обяд", dinner: "Вечеря", snack: "Междинни" },
  en: { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" },
  es: { breakfast: "Desayuno", lunch: "Almuerzo", dinner: "Cena", snack: "Merienda" },
};

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

function formatDate(iso, bg) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (iso === TODAY_ISO()) return bg ? "Днес" : "Today";
  if (d.getTime() === yesterday.getTime()) return bg ? "Вчера" : "Yesterday";
  const months_bg = ["яну","фев","март","апр","май","юни","юли","авг","сеп","окт","ное","дек"];
  const months_en = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const m = bg ? months_bg : months_en;
  return `${d.getDate()} ${m[d.getMonth()]}`;
}

export default function MealHistoryPanel({ onCopied, variant = "desktop" }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const L = MEAL_LABELS[bg ? "bg" : "en"];
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [copying, setCopying] = useState(null);
  const [saveTemplate, setSaveTemplate] = useState(null); // {source_date} when dialog open
  const [templateName, setTemplateName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!expanded) return;
    setLoading(true);
    try {
      const [hRes, tRes] = await Promise.all([
        axios.get(`${API}/meals/history`, { params: { days: 14 } }),
        axios.get(`${API}/meals/template/list`),
      ]);
      setDays(hRes.data.days || []);
      setTemplates(tRes.data.templates || []);
    } catch (e) {
      toast.error(bg ? "Грешка при зареждане" : "Error loading history");
    }
    setLoading(false);
  }, [API, expanded, bg]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Listen for meal updates to refresh
  useEffect(() => {
    const handler = () => { if (expanded) fetchHistory(); };
    window.addEventListener("meal-added", handler);
    return () => window.removeEventListener("meal-added", handler);
  }, [expanded, fetchHistory]);

  const copyMeal = async (sourceDate, mealType) => {
    setCopying(`${sourceDate}-${mealType}`);
    try {
      const { data } = await axios.post(`${API}/meals/copy-meal`, {
        source_date: sourceDate,
        meal_type: mealType,
        target_date: TODAY_ISO(),
      });
      toast.success(bg ? `Копирани ${data.copied_count} елемента към ${L[mealType]} днес` : `Copied ${data.copied_count} items to today's ${L[mealType]}`);
      onCopied?.();
      window.dispatchEvent(new Event("meal-added"));
    } catch (e) {
      toast.error(bg ? "Грешка при копиране" : "Copy failed");
    }
    setCopying(null);
  };

  const copyDay = async (sourceDate) => {
    setCopying(`day-${sourceDate}`);
    try {
      const { data } = await axios.post(`${API}/meals/copy-day`, {
        source_date: sourceDate,
        target_date: TODAY_ISO(),
      });
      toast.success(bg ? `Копиран цял ден (${data.copied_count} елемента) към днес` : `Copied full day (${data.copied_count} items) to today`);
      onCopied?.();
      window.dispatchEvent(new Event("meal-added"));
    } catch (e) {
      toast.error(bg ? "Грешка при копиране" : "Copy failed");
    }
    setCopying(null);
  };

  const openSaveTemplate = (sourceDate) => {
    const defaultName = bg ? `${formatDate(sourceDate, bg)} ден` : `${formatDate(sourceDate, bg)} day`;
    setTemplateName(defaultName);
    setSaveTemplate({ source_date: sourceDate });
  };

  const doSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTpl(true);
    try {
      const { data } = await axios.post(`${API}/meals/template/save`, {
        source_date: saveTemplate.source_date,
        name: templateName.trim(),
      });
      toast.success(bg ? `Шаблон "${data.template.name}" запазен` : `Template "${data.template.name}" saved`);
      setTemplates(prev => [data.template, ...prev]);
      setSaveTemplate(null);
      setTemplateName("");
    } catch (e) {
      toast.error(bg ? "Грешка при запазване" : "Save failed");
    }
    setSavingTpl(false);
  };

  const applyTemplate = async (templateId, tplName) => {
    setCopying(`tpl-${templateId}`);
    try {
      const { data } = await axios.post(`${API}/meals/template/apply`, {
        template_id: templateId,
        target_date: TODAY_ISO(),
      });
      toast.success(bg ? `"${tplName}" приложен (${data.applied_count} елемента)` : `"${tplName}" applied (${data.applied_count} items)`);
      onCopied?.();
      window.dispatchEvent(new Event("meal-added"));
    } catch (e) {
      toast.error(bg ? "Грешка" : "Failed");
    }
    setCopying(null);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/meals/template/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success(bg ? "Шаблон изтрит" : "Template deleted");
    } catch {
      toast.error(bg ? "Грешка" : "Failed");
    }
  };

  // Quick stats when collapsed
  const weekTotals = days.slice(0, 7).reduce((acc, d) => {
    acc.kcal += d.totals.calories || 0;
    acc.dil += d.totals.dil || 0;
    acc.daysLogged += (d.totals.calories > 0 ? 1 : 0);
    return acc;
  }, { kcal: 0, dil: 0, daysLogged: 0 });

  return (
    <>
    <CollapsiblePanel
      title={bg ? "Седмичен преглед" : "Weekly Overview"}
      subtitle={weekTotals.daysLogged > 0 ? `${weekTotals.daysLogged}${bg ? " дни" : " days"} · ${Math.round(weekTotals.kcal)} kcal` : undefined}
      icon={History}
      iconColor="#8B5CF6"
      credits={0}
      defaultOpen={false}
      storageKey={`meal-history-${variant}`}
      testId={`meal-history-panel${variant === "mobile" ? "-m" : ""}`}
    >
      <div className="space-y-2" data-testid="meal-history-content">
          {/* Saved Templates */}
          {templates.length > 0 && (
            <div className={`rounded-xl border p-3 ${isDark ? 'border-[hsl(223,47%,18%)] bg-[hsl(223,47%,12%)]' : 'border-purple-100 bg-purple-50/30'}`} data-testid="templates-section">
              <div className="flex items-center gap-1.5 mb-2">
                <Bookmark className="w-3.5 h-3.5 text-[#8B5CF6]" />
                <span className={`text-xs font-bold ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}>
                  {bg ? "Запазени шаблони" : "Saved templates"}
                </span>
              </div>
              <div className="space-y-1.5">
                {templates.map(t => (
                  <div key={t.id} className={`flex items-center justify-between gap-2 p-2 rounded-lg ${isDark ? 'bg-[hsl(223,47%,14%)]' : 'bg-white'}`} data-testid={`template-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}>{t.name}</p>
                      <div className={`flex gap-2 text-[10px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        <span>{t.entry_count} {bg ? "храни" : "items"}</span>
                        <span>{Math.round(t.totals?.calories || 0)} kcal</span>
                        {t.totals?.dil > 0 && (
                          <span className="font-bold text-red-500">DIL {t.totals.dil.toFixed(1)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className={`h-7 px-2 text-[10px] ${isDark ? 'hover:bg-[hsl(223,47%,20%)] text-[#10B981]' : 'hover:bg-green-50 text-[#10B981]'}`}
                        onClick={() => applyTemplate(t.id, t.name)}
                        disabled={copying === `tpl-${t.id}`}
                        data-testid={`apply-template-${t.id}`}>
                        {copying === `tpl-${t.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                        {bg ? "Приложи" : "Apply"}
                      </Button>
                      <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${isDark ? 'hover:bg-red-900/20 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                        onClick={() => deleteTemplate(t.id)}
                        data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className={`w-5 h-5 animate-spin ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
          )}
          {!loading && days.length === 0 && (
            <div className="text-center py-6">
              <Calendar className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {bg ? "Няма история на хранене" : "No meal history yet"}
              </p>
            </div>
          )}
          {!loading && days.map(day => {
            const isToday = day.date === TODAY_ISO();
            const isOpen = expandedDay === day.date;
            const dayKey = `day-${day.date}`;
            const hasEntries = day.totals.calories > 0;

            return (
              <div key={day.date} className={`rounded-xl border overflow-hidden ${isDark ? 'border-[hsl(223,47%,18%)]' : 'border-gray-100'}`} data-testid={`history-day-${day.date}`}>
                {/* Day header */}
                <div className={`p-2.5 cursor-pointer ${
                  isOpen ? (isDark ? 'bg-[hsl(223,47%,14%)]' : 'bg-gray-50') : (isDark ? 'hover:bg-[hsl(223,47%,12%)]' : 'hover:bg-gray-50')
                }`} onClick={() => setExpandedDay(isOpen ? null : day.date)}>
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold truncate ${isToday ? 'text-[#0EA5E9]' : (isDark ? 'text-gray-200' : 'text-[#1A1A2E]')}`}>
                        {formatDate(day.date, bg)}
                      </p>
                      <div className={`flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <span>{Math.round(day.totals.calories)} kcal</span>
                        <span>P:{Math.round(day.totals.protein)}g</span>
                        <span>C:{Math.round(day.totals.carbs)}g</span>
                        <span>F:{Math.round(day.totals.fat)}g</span>
                        {day.totals.dil > 0 && (
                          <span className={`font-bold px-1 rounded ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-500'}`}>
                            DIL {day.totals.dil.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {hasEntries && !isToday && (
                    <div className="flex items-center justify-end gap-1.5 mt-2 flex-wrap">
                      <Button size="sm" variant="ghost" className={`h-7 px-2 text-[10px] ${isDark ? 'hover:bg-[hsl(223,47%,20%)] text-[#F59E0B]' : 'hover:bg-amber-50 text-[#F59E0B]'}`}
                        onClick={(e) => { e.stopPropagation(); openSaveTemplate(day.date); }}
                        data-testid={`save-template-${day.date}`}>
                        <Bookmark className="w-3 h-3 mr-1" />
                        {bg ? "Шаблон" : "Template"}
                      </Button>
                      <Button size="sm" variant="ghost" className={`h-7 px-2 text-[10px] ${isDark ? 'hover:bg-[hsl(223,47%,20%)] text-[#8B5CF6]' : 'hover:bg-purple-50 text-[#8B5CF6]'}`}
                        onClick={(e) => { e.stopPropagation(); copyDay(day.date); }}
                        disabled={copying === dayKey}
                        data-testid={`copy-day-${day.date}`}>
                        {copying === dayKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3 mr-1" />}
                        {bg ? "Копирай ден" : "Copy day"}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Expanded meal list */}
                {isOpen && hasEntries && (
                  <div className={`px-2 pb-2 space-y-1 ${isDark ? 'bg-[hsl(223,47%,11%)]' : 'bg-white'}`}>
                    {["breakfast", "lunch", "dinner", "snack"].map(mt => {
                      const entries = day.meals[mt] || [];
                      if (entries.length === 0) return null;
                      const mealKey = `${day.date}-${mt}`;
                      const mealCals = entries.reduce((s, e) => s + (e.calories || 0), 0);

                      return (
                        <div key={mt} className={`rounded-lg p-2 ${isDark ? 'bg-[hsl(223,47%,14%)]' : 'bg-gray-50'}`} data-testid={`history-meal-${day.date}-${mt}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <Flame className="w-3 h-3 text-[#F59E0B]" />
                              <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}>{L[mt]}</span>
                              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{Math.round(mealCals)} kcal</span>
                            </div>
                            {!isToday && (
                              <Button size="sm" variant="ghost" className={`h-6 px-1.5 text-[9px] ${isDark ? 'hover:bg-[hsl(223,47%,20%)] text-[#0EA5E9]' : 'hover:bg-blue-50 text-[#0EA5E9]'}`}
                                onClick={() => copyMeal(day.date, mt)}
                                disabled={copying === mealKey}
                                data-testid={`copy-meal-${day.date}-${mt}`}>
                                {copying === mealKey ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Copy className="w-2.5 h-2.5 mr-0.5" />}
                                {bg ? "Копирай" : "Copy"}
                              </Button>
                            )}
                          </div>
                          <ul className="space-y-0.5 pl-4">
                            {entries.map(e => (
                              <li key={e.id} className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                <span className="font-medium">{e.food_name}</span>
                                <span className={`ml-1.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>— {Math.round(e.amount_g)}g ({Math.round(e.calories)} kcal)</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
                {isOpen && !hasEntries && (
                  <div className={`p-3 text-center text-[10px] ${isDark ? 'text-gray-600 bg-[hsl(223,47%,11%)]' : 'text-gray-400 bg-white'}`}>
                    {bg ? "Няма записани хранения този ден" : "No meals logged this day"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
    </CollapsiblePanel>

      {/* Save Template Dialog */}
      {saveTemplate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !savingTpl && setSaveTemplate(null)}>
          <div className={`w-full max-w-sm mx-4 rounded-2xl p-5 ${isDark ? 'bg-[hsl(222,47%,11%)] border border-[hsl(223,47%,20%)]' : 'bg-white shadow-xl'}`}
            onClick={e => e.stopPropagation()} data-testid="save-template-dialog">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-[#8B5CF6]" />
                <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
                  {bg ? "Запази като шаблон" : "Save as template"}
                </h3>
              </div>
              <button onClick={() => !savingTpl && setSaveTemplate(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {bg ? `Всички хранения от ${formatDate(saveTemplate.source_date, bg)} ще бъдат запазени като преизползваем шаблон.`
                  : `All meals from ${formatDate(saveTemplate.source_date, bg)} will be saved as a reusable template.`}
            </p>
            <Input
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder={bg ? "Напр. Моят low-DIL ден" : "e.g., My low-DIL day"}
              className={`h-10 text-sm mb-3 ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`}
              data-testid="template-name-input"
              autoFocus
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSaveTemplate(null)} disabled={savingTpl}
                className={`flex-1 h-10 text-sm ${isDark ? 'border-[hsl(223,47%,20%)]' : ''}`}>
                {bg ? "Отказ" : "Cancel"}
              </Button>
              <Button onClick={doSaveTemplate} disabled={!templateName.trim() || savingTpl}
                className="flex-1 h-10 gradient-primary text-white text-sm rounded-xl" data-testid="save-template-confirm">
                {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : (bg ? "Запази" : "Save")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
